<template>
  <div>
    <v-card class="mb-4 d-print-none">
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-bus-clock</v-icon>
        {{ $t('today_on_the_road') }}
        <v-spacer></v-spacer>
        <span class="text-body-2 me-4">{{ date }}</span>
        <v-btn icon :loading="isLoading" @click="load">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-subtitle>{{ $t('today_on_the_road_hint') }}</v-card-subtitle>

      <v-card-text>
        <v-row dense>
          <v-col v-for="tile in tiles" :key="tile.key" cols="6" md="3" lg="2">
            <v-sheet rounded outlined class="pa-3 text-center">
              <div class="text-h5" :class="tile.colour ? tile.colour + '--text' : ''">
                {{ tile.value }}
              </div>
              <div class="text-caption">{{ tile.label }}</div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="d-print-none">
      <vue-element-loading :active="isLoading" />

      <v-data-table
        :headers="headers"
        :items="runs"
        :items-per-page="25"
        :no-data-text="$t('nothing_running_today')"
        dense
      >
        <template #[`item.state`]="{ item }">
          <v-chip x-small :color="stateColour(item)" dark>
            {{ stateLabel(item) }}
          </v-chip>
        </template>

        <template #[`item.progress`]="{ item }">
          <div class="d-flex align-center" style="min-width: 120px">
            <v-progress-linear
              :value="item.stops_total ? (item.stops_done / item.stops_total) * 100 : 0"
              height="8"
              rounded
              :color="item.state === 'finished' ? 'success' : 'primary'"
              class="me-2"
            ></v-progress-linear>
            <span class="text-caption text-no-wrap">{{ item.stops_done }}/{{ item.stops_total }}</span>
          </div>
        </template>

        <!-- the numbers a school is asked for on the telephone -->
        <template #[`item.children`]="{ item }">
          <span class="text-no-wrap">
            <v-chip x-small class="me-1" outlined>{{ item.students_total }}</v-chip>
            <v-chip v-if="item.students_aboard" x-small class="me-1" color="primary" dark>
              {{ $t('aboard_n', { count: item.students_aboard }) }}
            </v-chip>
            <v-chip v-if="item.students_missed" x-small color="error" dark>
              {{ $t('missed_n', { count: item.students_missed }) }}
            </v-chip>
          </span>
        </template>

        <template #[`item.actions`]="{ item }">
          <v-btn small text color="primary" @click="openRun(item)">
            <v-icon small left>mdi-clipboard-list-outline</v-icon>
            {{ $t('manifest') }}
          </v-btn>
          <!-- the driver is ill, or the bus will not start -->
          <v-btn
            v-if="item.state !== 'finished'"
            small
            text
            @click="openCover(item)"
          >
            <v-icon small left>mdi-account-switch</v-icon>
            {{ $t('cover') }}
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- covering one run, without changing the trip it came from -->
    <v-dialog v-model="coverDialog" max-width="520" no-click-animation @keydown.esc="coverDialog = false">
      <v-card v-if="coveringRun">
        <v-card-title>
          <v-icon color="primary" class="me-2">mdi-account-switch</v-icon>
          <span class="text-h6">{{ $t('cover_this_run') }}</span>
        </v-card-title>
        <v-card-subtitle class="pt-2">
          {{ $t('cover_hint', { route: coveringRun.route || $t('run'), date: date }) }}
        </v-card-subtitle>

        <v-card-text>
          <v-select
            v-model="coverDriverId"
            :items="driverOptions"
            :label="$t('cover_driver')"
            item-text="text"
            item-value="value"
            clearable
            dense
            outlined
            hide-details
            class="mb-4"
          ></v-select>
          <v-select
            v-model="coverBusId"
            :items="busOptions"
            :label="$t('cover_bus')"
            item-text="text"
            item-value="value"
            clearable
            dense
            outlined
            :hint="$t('cover_bus_hint')"
            persistent-hint
          ></v-select>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="isCovering" @click="coverDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="primary"
            text
            :loading="isCovering"
            :disabled="!coverDriverId && !coverBusId"
            @click="saveCover"
          >
            {{ $t('cover') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- the manifest, which is also what gets printed -->
    <v-dialog v-model="runDialog" max-width="900" no-click-animation @keydown.esc="runDialog = false">
      <v-card v-if="openedRun" id="manifest">
        <v-card-title>
          <v-icon color="primary" class="me-2 d-print-none">mdi-clipboard-list-outline</v-icon>
          <span>{{ openedRun.route || $t('run') }}</span>
          <v-spacer></v-spacer>
          <v-btn class="d-print-none" icon @click="print">
            <v-icon>mdi-printer</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          {{ $t('manifest_line', {
            date: date,
            driver: openedRun.driver || '-',
            bus: openedRun.bus || '-',
            start: openedRun.planned_start || '-',
          }) }}
        </v-card-subtitle>

        <v-card-text>
          <vue-element-loading :active="isLoadingRun" />

          <div v-for="stop in runStops" :key="stop.stop_id" class="mb-4">
            <div class="d-flex align-center">
              <v-icon small class="me-2">mdi-bus-stop</v-icon>
              <strong>{{ stop.name || $t('stop') }}</strong>
              <span class="text-caption ms-2">{{ stop.planned_time }}</span>
              <v-spacer></v-spacer>
              <v-chip x-small outlined>{{ stop.student_count }}</v-chip>
            </div>
            <div class="text-caption mb-1">{{ stop.address }}</div>

            <v-simple-table v-if="stop.students.length" dense>
              <template #default>
                <thead>
                  <tr>
                    <th>{{ $t('Name') }}</th>
                    <th>{{ $t('student_id') }}</th>
                    <th>{{ $t('notes') }}</th>
                    <th>{{ $t('contact') }}</th>
                    <th>{{ $t('status') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="student in stop.students" :key="student.id">
                    <td>{{ student.name }}</td>
                    <td>{{ student.student_identification }}</td>
                    <td>{{ student.notes }}</td>
                    <td>
                      <span v-if="student.contact">
                        {{ student.contact.name }}
                        <span v-if="student.contact.tel_number"> — {{ student.contact.tel_number }}</span>
                      </span>
                    </td>
                    <td>{{ $t('ride_status_' + student.ride_status) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
            <p v-else class="text-caption mb-0">{{ $t('nobody_gets_on_here') }}</p>
          </div>
        </v-card-text>

        <v-card-actions class="d-print-none">
          <v-spacer></v-spacer>
          <v-btn text @click="runDialog = false">{{ $t('close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/*
 * The morning itself.
 *
 * The readiness board says what will go wrong tomorrow; on the day, a school
 * had a map with buses on it and nothing else. Whether a run had started,
 * whether it was late, how far along it was, how many children were aboard and
 * which were missed - all of it was in the database and on no screen, so the
 * answer to a parent telephoning was to open the tracking map and count.
 *
 * The manifest behind each run is the same data in the form a school actually
 * uses it: stop by stop, with the children at each and the family to call.
 * Printable, because when the app fails paper is what the driver gets.
 */
import VueElementLoading from "vue-element-loading";

export default {
  name: "OperationsIndex",
  components: { VueElementLoading },
  data() {
    return {
      date: "",
      runs: [],
      totals: {},
      isLoading: false,
      isLoadingRun: false,
      runDialog: false,
      openedRun: null,
      runStops: [],
      timer: null,
      coverDialog: false,
      coveringRun: null,
      coverDriverId: null,
      coverBusId: null,
      isCovering: false,
      drivers: [],
      buses: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: this.$t('route'), value: 'route' },
        { text: this.$t('planned_start'), value: 'planned_start', width: 110 },
        { text: this.$t('driver'), value: 'driver' },
        { text: this.$t('bus'), value: 'bus', width: 110 },
        { text: this.$t('status'), value: 'state', width: 120 },
        { text: this.$t('progress'), value: 'progress', sortable: false },
        { text: this.$t('children'), value: 'children', sortable: false },
        { text: '', value: 'actions', sortable: false, width: 130 },
      ];
    },
    driverOptions() {
      return this.drivers.map((driver) => ({ text: driver.name, value: driver.id }));
    },
    busOptions() {
      return this.buses.map((bus) => ({
        text: bus.license + (bus.capacity ? " (" + bus.capacity + ")" : ""),
        value: bus.id,
      }));
    },
    tiles() {
      const t = this.totals || {};
      return [
        { key: 'runs', label: this.$t('runs_today'), value: t.runs || 0 },
        { key: 'late', label: this.$t('late'), value: t.late || 0, colour: t.late ? 'error' : '' },
        { key: 'running', label: this.$t('on_the_road'), value: t.running || 0, colour: 'primary' },
        { key: 'finished', label: this.$t('finished'), value: t.finished || 0, colour: 'success' },
        { key: 'expected', label: this.$t('children_expected'), value: t.children_expected || 0 },
        { key: 'missed', label: this.$t('children_missed'), value: t.children_missed || 0, colour: t.children_missed ? 'error' : '' },
      ];
    },
  },
  mounted() {
    this.load();
    //a morning screen that has to be refreshed by hand is a morning screen
    //nobody leaves open
    this.timer = window.setInterval(this.load, 60000);
  },
  beforeDestroy() {
    if (this.timer) window.clearInterval(this.timer);
  },
  methods: {
    stateColour(run) {
      if (run.is_late) return 'error';
      return { not_started: 'grey', running: 'primary', finished: 'success' }[run.state] || 'grey';
    },
    stateLabel(run) {
      if (run.is_late) return this.$t('late');
      return this.$t('run_state_' + run.state);
    },
    load() {
      this.isLoading = true;
      axios
        .get("/school-operations/today")
        .then((response) => {
          this.date = response.data.date;
          this.runs = response.data.runs || [];
          this.totals = response.data.totals || {};
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_today"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    openRun(run) {
      this.openedRun = run;
      this.runStops = [];
      this.runDialog = true;
      this.isLoadingRun = true;

      axios
        .get("/school-operations/run", { params: { planned_trip_id: run.id } })
        .then((response) => {
          this.runStops = response.data.stops || [];
          this.openedRun = response.data.run || run;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_run"));
        })
        .then(() => {
          this.isLoadingRun = false;
        });
    },
    /*
     * Covering a run.
     *
     * Changing the trip's driver does nothing for today - today's run was
     * published hours ago with a driver written into it, and that row is what
     * the driver's phone reads. This changes the run, so the cover driver sees
     * it within the minute and tomorrow is still the regular driver's.
     */
    openCover(run) {
      this.coveringRun = run;
      this.coverDriverId = null;
      this.coverBusId = null;
      this.coverDialog = true;

      if (!this.drivers.length) {
        axios
          .get("/users/all-drivers")
          .then((response) => {
            this.drivers = response.data.drivers || response.data || [];
          })
          .catch(() => {});
      }
      if (!this.buses.length) {
        axios
          //every bus the school owns, including the spare in the yard with
          //no driver on it - which is exactly the one a breakdown needs
          .get("/buses/all")
          .then((response) => {
            this.buses = response.data.buses || response.data || [];
          })
          .catch(() => {});
      }
    },
    saveCover() {
      this.isCovering = true;
      axios
        .post("/school-operations/cover", {
          planned_trip_id: this.coveringRun.id,
          driver_id: this.coverDriverId || null,
          bus_id: this.coverBusId || null,
        })
        .then((response) => {
          this.$notifySuccess((response.data.success || []).join(", "));
          this.coverDialog = false;
          this.load();
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_covering_run"));
        })
        .then(() => {
          this.isCovering = false;
        });
    },
    print() {
      window.print();
    },
  },
};
</script>

<style>
/* The manifest is the only thing worth putting on paper, so everything else
   leaves the page rather than being printed around it. */
@media print {
  .v-navigation-drawer,
  .v-app-bar,
  .v-footer,
  .d-print-none {
    display: none !important;
  }
  .v-dialog__content,
  .v-dialog {
    position: static !important;
    box-shadow: none !important;
    max-width: 100% !important;
  }
  .v-main {
    padding: 0 !important;
  }
}
</style>
